import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus } from "lucide-react";

import { NewColorDialog } from "@/components/catalog/NewColorDialog";
import { NewDesignDialog } from "@/components/catalog/NewDesignDialog";

const TH = "text-xs uppercase tracking-widest text-muted-foreground";
const FORMATS = [["MZ", "4×4"], ["BJ", "2×6"], ["MD", "2×2"]];

const StatusBadge = ({ p, testid }) => (
  <span className={`badge-status ${p.archived ? "badge-reserved" : p.active ? "badge-available" : "badge-neutral"}`} data-testid={testid}>
    {p.archived ? "archived" : p.active ? "active" : "inactive"}
  </span>
);

const Empty = ({ cols, text, testid }) => (
  <TableRow><TableCell colSpan={cols} className="py-12 text-center text-sm text-muted-foreground" data-testid={testid}>{text}</TableCell></TableRow>
);

// ID-064 Tab 1 — standard Zellige master colors (each with MZ/BJ/MD/TAL canonical variants)
const ZelligeTab = ({ rows, loading, onOpen }) => (
  <div className="overflow-x-auto rounded-md border border-border bg-white shadow-[0_2px_12px_rgba(26,26,24,0.04)]">
    <Table data-testid="zellige-table">
      <TableHeader>
        <TableRow className="border-border">
          <TableHead className={TH}>Code</TableHead>
          <TableHead className={TH}>Moroccan name</TableHead>
          <TableHead className={`${TH} hidden lg:table-cell`}>U.S. name</TableHead>
          <TableHead className={`${TH} hidden xl:table-cell`}>Formats</TableHead>
          <TableHead className={TH}>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading ? <Empty cols={5} text="Loading…" /> : rows.length === 0 ? <Empty cols={5} text="No master colors yet." testid="zellige-empty" /> : rows.map((p) => (
          <TableRow key={p.id} data-testid={`zellige-row-${p.color_code || p.id}`} onClick={() => onOpen(p)} className="cursor-pointer border-border transition-colors duration-200 hover:bg-sand">
            <TableCell className="py-4 font-mono text-sm font-semibold text-terracotta">{p.color_code || "—"}</TableCell>
            <TableCell className="py-4 font-medium text-charcoal">
              {p.moroccan_name || p.name}
              <span className="mt-0.5 block text-xs font-normal lg:hidden">{p.us_color_name || <span className="text-warmgrey">U.S. name pending</span>}</span>
            </TableCell>
            <TableCell className="hidden py-4 lg:table-cell" data-testid={`zellige-us-${p.color_code}`}>
              {p.us_color_name ? <span className="font-semibold text-charcoal">{p.us_color_name}</span> : <span className="badge-status badge-reserved">U.S. name pending</span>}
            </TableCell>
            <TableCell className="hidden py-4 xl:table-cell" data-testid={`zellige-formats-${p.color_code}`}>
              <div className="flex flex-wrap gap-1">
                {FORMATS.map(([px, label]) => (
                  <span key={px} title={label} className={`rounded px-1.5 py-0.5 font-mono text-[10px] ${p.format_prefixes?.includes(px) ? "bg-[#96A58F]/25 text-charcoal" : "bg-sand text-warmgrey line-through"}`}>{px}</span>
                ))}
                <span className="ml-1 text-xs text-warmgrey">{p.variant_count}</span>
              </div>
            </TableCell>
            <TableCell className="py-4"><StatusBadge p={p} testid={`zellige-status-${p.color_code}`} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

// ID-064 Tab 2 — Zelligio-owned mosaic collections (design records); dealer-exclusive Custom Studio designs never appear here
const MosaicTab = ({ rows, loading, onOpen, onAdd }) => (
  <div className="overflow-x-auto rounded-md border border-border bg-white shadow-[0_2px_12px_rgba(26,26,24,0.04)]">
    <Table data-testid="mosaic-table">
      <TableHeader>
        <TableRow className="border-border">
          <TableHead className={TH}>Collection</TableHead>
          <TableHead className={`${TH} hidden xl:table-cell`}>Family</TableHead>
          <TableHead className={TH}>Variations</TableHead>
          <TableHead className={`${TH} hidden lg:table-cell`}>Dealer-visible</TableHead>
          <TableHead className={TH}>Status</TableHead>
          <TableHead className={TH}><span className="sr-only">Actions</span></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading ? <Empty cols={6} text="Loading…" /> : rows.length === 0 ? <Empty cols={6} text="No mosaic collections yet." testid="mosaic-empty" /> : rows.map((p) => (
          <TableRow key={p.code} data-testid={`mosaic-row-${p.code}`} onClick={() => onOpen(p)} className="cursor-pointer border-border transition-colors duration-200 hover:bg-sand">
            <TableCell className="py-4 font-display text-lg font-medium text-charcoal">{p.name}<span className="block font-sans text-[11px] text-warmgrey">{[p.tile_size_in && `Tile ${p.tile_size_in}`, p.mesh_sheet_in && `Mesh sheet ${p.mesh_sheet_in}`].filter(Boolean).join(" · ")}</span></TableCell>
            <TableCell className="hidden py-4 text-sm text-warmgrey xl:table-cell" data-testid={`mosaic-family-${p.code}`}>{p.material ? `Material ${p.material}` : "Mosaic Collection"}</TableCell>
            <TableCell className="num py-4 text-sm text-charcoal" data-testid={`mosaic-variations-${p.code}`}>{p.variant_count}<span className="block text-[11px] text-warmgrey lg:hidden">{p.dealer_visible_count} dealer-visible</span></TableCell>
            <TableCell className="hidden py-4 lg:table-cell" data-testid={`mosaic-visible-${p.code}`}>
              {p.dealer_visible_count > 0
                ? <span className="text-sm text-charcoal">{p.dealer_visible_count} of {p.variant_count}</span>
                : <span className="badge-status badge-neutral">Not in dealer Catalog</span>}
            </TableCell>
            <TableCell className="py-4"><span className={`badge-status ${p.family_status === "ALL_ACTIVE" ? "badge-available" : "badge-neutral"}`} data-testid={`mosaic-status-${p.code}`}>{{ ALL_ACTIVE: "All active", PARTIAL: "Partially active", ALL_INACTIVE: "All inactive" }[p.family_status]}</span></TableCell>
            <TableCell className="py-4 text-right">
              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onAdd(p); }} data-testid={`mosaic-add-variation-${p.code}`} className="h-8 whitespace-nowrap px-2 sm:px-3" aria-label="Add variation">
                <Plus className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Variation</span>
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export default function Products() {
  const [products, setProducts] = useState([]);
  const [mosaics, setMosaics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [colorOpen, setColorOpen] = useState(false);
  const [designOpen, setDesignOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "mosaic" ? "mosaic" : "zellige";
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/products", { params: { include_archived: showArchived } }), api.get("/admin/mosaic-collections")])
      .then(([p, d]) => { setProducts(p.data); setMosaics(d.data); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const zellige = products.filter((p) => p.family_group === "ZELLIGE_TILE")
    .sort((a, b) => String(a.color_code || a.name).localeCompare(String(b.color_code || b.name), undefined, { numeric: true }));
  const open = (p) => navigate(`/admin/products/${p.id}`);
  const openCollection = (p, add) => navigate(`/admin/collections/${encodeURIComponent(p.code)}${add ? "?add=1" : ""}`);

  return (
    <div data-testid="admin-products-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Administration</p>
          <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Products</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-warmgrey" data-testid="products-subtitle">
            Organised like the dealer Catalog: Zellige Tiles (master colors with their 4×4, 2×6 and 2×2 variants) and Zelligio Mosaic Collections (DAMA, TALIA, FINA and future collections — shared specifications and dealer prices set once per collection). New Color still creates each color's single-color TALIA variation automatically. Dealer-exclusive Custom Studio designs stay in Custom Studio.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-warmgrey">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="products-show-archived" /> Show archived
        </label>
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams(v === "mosaic" ? { tab: "mosaic" } : {}, { replace: true })} className="mt-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <TabsList className="h-11 bg-sand" data-testid="products-tabs">
            <TabsTrigger value="zellige" data-testid="tab-zellige" className="px-4">Zellige Tiles <span className="ml-2 text-xs text-warmgrey">{zellige.length}</span></TabsTrigger>
            <TabsTrigger value="mosaic" data-testid="tab-mosaic" className="px-4">Mosaic Collections <span className="ml-2 text-xs text-warmgrey">{mosaics.length}</span></TabsTrigger>
          </TabsList>
          {tab === "zellige" ? (
            <Button onClick={() => setColorOpen(true)} data-testid="new-color-button" className="h-11 bg-terracotta text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-terracotta-hover">
              <Plus className="mr-2 h-4 w-4" /> New Color
            </Button>
          ) : (
            <Button onClick={() => setDesignOpen(true)} data-testid="new-mosaic-design-button" className="h-11 bg-terracotta text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-terracotta-hover">
              <Plus className="mr-2 h-4 w-4" /> New Mosaic Design
            </Button>
          )}
        </div>
        <TabsContent value="zellige" className="mt-6"><ZelligeTab rows={zellige} loading={loading} onOpen={open} /></TabsContent>
        <TabsContent value="mosaic" className="mt-6"><MosaicTab rows={mosaics} loading={loading} onOpen={(p) => openCollection(p)} onAdd={(p) => openCollection(p, true)} /></TabsContent>
      </Tabs>

      <NewColorDialog open={colorOpen} onOpenChange={setColorOpen} onCreated={load} />
      <NewDesignDialog open={designOpen} onOpenChange={setDesignOpen} onCreated={load} />
    </div>
  );
}
